import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';

import { DPU_ERROR_DESCRIPTIONS } from 'shared-business/communication';
import { I18nService } from 'shared-modules-angular/i18n';
import { SnackbarService } from 'shared-modules-angular/snackbar';
import { nonNullish } from 'shared-utils/data';
import { isolateFilename } from 'shared-utils/file';

import { LAST_SETUP_FILE_NAME_STORAGE_KEY, LAST_SETUP_FILE_PATH_STORAGE_KEY, LOCAL_STORAGE_SERVICE, SAVE_FILE_SERVICE, SHUTDOWN_SERVICE } from '../dpu-file.token';
import { getAutoNamingFileName } from '../helpers';
import { DpuFileSystemService } from '../services';
import { ILocalStorageService } from '../services/local-storage.service';
import { ISaveFileService } from '../services/save-file.service';
import { IShutdownService } from '../services/shutdown.service';
import { SaveDpuFileActions } from './dpu-file.action';
import { dpuFileFeature } from './dpu-file.feature';

export const PERSISTENT_FILE_NAME = 'persistent-file.uset';

@Injectable()
export class DpuFileEffects {
  public saveAsWithAutoNaming$ = createEffect(() => this._actions$.pipe(
    ofType(SaveDpuFileActions.saveAsWithAutoNaming),
    concatLatestFrom(() => this._store.select(dpuFileFeature.selectDpuFileNameWithoutExtension)),
    switchMap(([{ fileType }, fileName]) => {
      if (fileName) {
        fileName = this.replacePersistentFileName(fileName);
      }

      return [
        SaveDpuFileActions.save({ fileName: getAutoNamingFileName(fileName, fileType), fileType }),
      ];
    }),
  ));

  public saveSetupInPersistentFile$ = createEffect(() => this._actions$.pipe(
    ofType(SaveDpuFileActions.saveSetupInPersistentFile),
    switchMap(({ shouldScheduleShutdown }) => this._dpuFileSystemService.getUserFilePathByFileType('temp').pipe(
      map(folder => ({ folder, shouldScheduleShutdown })),
    )),
    map(({ folder, shouldScheduleShutdown }) => ({
      path: folder ? `${folder}/${PERSISTENT_FILE_NAME}` : PERSISTENT_FILE_NAME,
      fileName: PERSISTENT_FILE_NAME,
      shouldScheduleShutdown,
    })),
    concatLatestFrom(() => this._store.select(dpuFileFeature.selectAppMetadata)),
    switchMap(([{ path, shouldScheduleShutdown }, appMetadata]) => this._saveFileService.save(path, appMetadata, 'setup').pipe(
      map(() => SaveDpuFileActions.savePersistentFileSucceeded({ path, shouldScheduleShutdown })),
      catchError((error: HttpErrorResponse) => of(SaveDpuFileActions.saveFailed({
        error: { code: error.status, message: error.message },
        fileName: PERSISTENT_FILE_NAME,
      }))),
    )),
  ));

  public save$ = createEffect(() => this._actions$.pipe(
    ofType(SaveDpuFileActions.save),
    switchMap(({ fileName, fileType }) => this._dpuFileSystemService.getUserFilePathByFileType(fileType).pipe(
      map(folder => ({ fileName, folder, fileType })),
    )),
    map(({ fileName, folder, fileType }) => {
      fileName = this.replacePersistentFileName(fileName);

      return {
        path: folder ? `${folder}/${fileName}` : fileName,
        fileType,
        fileName,
      };
    }),
    concatLatestFrom(() => this._store.select(dpuFileFeature.selectAppMetadata)),
    switchMap(([{ path, fileType, fileName }, appMetadata]) => this._saveFileService.save(path, appMetadata, fileType).pipe(
      tap(() => {
        this._localStorageService.removeValue(this._lastSetupFilePathStorageKey);
        this._localStorageService.removeValue(this._lastSetupFileName);
      }),
      map(() => SaveDpuFileActions.saveSucceeded({ path, fileType, fileName })),
      catchError((error: HttpErrorResponse) => of(SaveDpuFileActions.saveFailed({
        error: { code: error.status, message: error.message },
        fileName,
      }))),
    )),
  ));

  public savePersistentFileSucceeded$ = createEffect(() => this._actions$.pipe(
    ofType(SaveDpuFileActions.savePersistentFileSucceeded),
    concatLatestFrom(() => this._store.select(dpuFileFeature.selectDpuFileNameWithoutExtension)),
    switchMap(([{ path, shouldScheduleShutdown }, fileName]) => {
      this._localStorageService.setValue(this._lastSetupFilePathStorageKey, path);

      // Prevents overwriting the original file name in local storage if the persistent file is saved multiple times.
      if (fileName && fileName !== isolateFilename(PERSISTENT_FILE_NAME)) {
        this._localStorageService.setValue(this._lastSetupFileName, fileName);
      }

      return shouldScheduleShutdown ? [SaveDpuFileActions.scheduleShutdown({ delayInSeconds: 5 })] : [];
    }),
  ));

  public saveSucceeded$ = createEffect(() => this._actions$.pipe(
    ofType(SaveDpuFileActions.saveSucceeded),
    tap(({ fileName }) => {
      this._snackbarService.open({
        color: 'success',
        message: this.getTranslationStream('SAVE.SUCCESS', { fileName }),
      });
    }),
  ), { dispatch: false });

  public saveFailed$ = createEffect(() => this._actions$.pipe(
    ofType(SaveDpuFileActions.saveFailed),
    tap(({ error, fileName }) => {
      const isPersistentFile = fileName === PERSISTENT_FILE_NAME;
      let message$: Observable<string>;

      if (nonNullish(DPU_ERROR_DESCRIPTIONS[error.code])) {
        message$ = this.getTranslationStream(DPU_ERROR_DESCRIPTIONS[error.code]);
      } else if (isPersistentFile) {
        message$ = this.getTranslationStream('SAVE.PERSISTENT_FILE_ERROR');
      } else {
        message$ = this.getTranslationStream('SAVE.ERROR', { fileName, error: error.message });
      }

      this._snackbarService.open({
        color: 'error',
        message: message$,
      });
    }),
  ), { dispatch: false });

  public scheduleShutdown$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SaveDpuFileActions.scheduleShutdown),
      tap(action => this._shutdownService.shutdown(action.delayInSeconds)),
    ), { dispatch: false },
  );

  constructor(
    private readonly _store: Store,
    private readonly _actions$: Actions,
    @Inject(SAVE_FILE_SERVICE) private readonly _saveFileService: ISaveFileService,
    private readonly _dpuFileSystemService: DpuFileSystemService,
    private readonly _snackbarService: SnackbarService,
    private readonly _i18nService: I18nService,
    @Inject(SHUTDOWN_SERVICE) private readonly _shutdownService: IShutdownService,
    @Inject(LAST_SETUP_FILE_PATH_STORAGE_KEY) private readonly _lastSetupFilePathStorageKey: string,
    @Inject(LAST_SETUP_FILE_NAME_STORAGE_KEY) private readonly _lastSetupFileName: string,
    @Inject(LOCAL_STORAGE_SERVICE) private readonly _localStorageService: ILocalStorageService,
  ) {
  }

  private replacePersistentFileName(fileName: string): string {
    const fileNameOnly = isolateFilename(PERSISTENT_FILE_NAME);
    if (fileName === fileNameOnly) {
      const lastSetupFileName = this._localStorageService.getValue(this._lastSetupFileName);
      if (lastSetupFileName) {
        fileName = lastSetupFileName;
      }
    }

    return fileName;
  }

  private getTranslationStream(message: string, translationParams?: { [key: string]: string }): Observable<string> {
    return this._i18nService.addTranslationStream(message, translationParams);
  }
}
